/**
 * Shared LTV (Loan-to-Value) badge used by the loan table and the term
 * deposit table.
 *
 * LOAN rows pass a `termEnded` boolean. Loans are non-amortising and
 * CANNOT be liquidated until the 3-year term finishes, so during the term
 * every loan is GREEN regardless of LTV (even maxed out). Once the term
 * ends the borrower must repay/exit:
 *   - within the term            →  green   "In Term"
 *   - term ended, < (liq − 5)%   →  orange  "At Risk"
 *   - term ended, ≥ (liq − 5)%   →  red     "Liquidation Risk"   (last 5%)
 * Per client (Jun 2026): "all green until the end of the 3 year term, then
 * Orange, and Red as they get to that last 5%."
 *
 * The term-deposit borrowing-capacity badge passes NO `termEnded` (null),
 * which keeps the legacy LTV risk bands (Safe / Watch / At Risk /
 * Liquidation) — that figure is a forward-looking capacity, not a live
 * liquidatable loan.
 *
 * When `unavailable` is true (e.g. a Turbo deposit whose Safe Vault is
 * already redeployed via the recursive leverage flow, so no fresh
 * borrowing capacity remains), render an "N/A" placeholder instead of
 * a meaningless 0% bar.
 */
import config from "../config/index.js";

const GREEN = { text: "text-[#16a34a]", bar: "bg-[#22c55e]" };
const AMBER = { text: "text-[#d97706]", bar: "bg-[#f59e0b]" };
const ORANGE = { text: "text-[#ea580c]", bar: "bg-[#f97316]" };
const RED = { text: "text-[#dc2626]", bar: "bg-[#ef4444]" };

export default function LtvBadge({ ltv, unavailable = false, termEnded = null, inTermLabel = "In Term" }) {
    if (unavailable) {
        return (
            <span className="font-manrope text-[12px] font-semibold text-black/50">
                N/A
            </span>
        );
    }
    const maxLtv = config.protocol.maxLtvBps / 100;
    const liqLtv = config.protocol.liquidationThresholdBps / 100;

    let label;
    let palette;
    if (termEnded !== null) {
        // Term-aware colouring — liquidation only after the term ends.
        // §M6 (Jul 2026 client review): "All Loans / LTV should show as Green
        // and Safe *until* day 1095 as there is no chance of liquidation
        // ahead of that." The My Term Deposits table used to fall through to
        // the legacy bands below and painted every leveraged Turbo child
        // amber "75% (Watch)"; it now passes `termEnded` too, with
        // `inTermLabel="Safe"` so the wording matches the client's ask while
        // the loans table keeps the "In Term" copy they liked.
        if (!termEnded) {
            label = inTermLabel;
            palette = GREEN;
        } else if (ltv >= liqLtv - 5) {
            label = "Liquidation Risk";
            palette = RED;
        } else {
            label = "Term Ended";
            palette = ORANGE;
        }
    } else {
        // Legacy LTV bands (term-deposit borrowing capacity).
        if (ltv <= 50) {
            label = "Safe";
            palette = GREEN;
        } else if (ltv <= maxLtv) {
            label = "Watch";
            palette = AMBER;
        } else if (ltv < liqLtv) {
            label = "At Risk";
            palette = ORANGE;
        } else {
            label = "Liquidation";
            palette = RED;
        }
    }

    return (
        <div className="flex flex-col gap-1">
            <span className={`font-manrope text-[12px] font-semibold ${palette.text}`}>
                {ltv}% ({label})
            </span>
            <span className="block h-1.5 w-20 overflow-hidden rounded-full bg-white/60">
                <span
                    className={`block h-full rounded-full ${palette.bar}`}
                    style={{ width: `${Math.min(100, ltv)}%` }}
                />
            </span>
        </div>
    );
}
